import { AfterViewInit, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import {
  MAT_TOOLTIP_DEFAULT_OPTIONS,
  MatTooltip,
  MatTooltipDefaultOptions,
  MatTooltipModule
} from '@angular/material/tooltip';
import { Store } from '@ngxs/store';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe, DatePipe, SlicePipe } from '@angular/common';
import {
  StateFilterCBComponent
} from '../../utils/component/state-filter-combobox/state-cb.component';
import { AuthState } from '../../core/authentication/auth.state';
import { FreeFilterInputComponent } from '../../utils/component/free-filter-input/free-filter-input.component';
import { BehaviorSubject, combineLatest, firstValueFrom, from, Observable, of, switchMap, tap } from 'rxjs';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { messageTypes, WidgetMessageService } from '../../core/services/widget-message.service';
import { ConsumerEntityService } from '../../consumer/services/consumer.entity.service';
import { SubjectEntityService } from '../../subject/services/subject.entity.service';
import { LearningUnitEntityService } from '../../learning-unit/services/learning-unit.entity.service';
import { MatDialog } from '@angular/material/dialog';
import { ModuleEntity } from '../module.entity';
import { ConsumerEntity } from '../../consumer/consumer.entity';
import { SubjectEntity } from '../../subject/subject.entity';
import { LearningUnitEntity } from '../../learning-unit/learning-unit-entity';
import { map } from 'rxjs/operators';
import { EntityStateModel, InvalidateLoadedQueries } from '../../core/state/entity.state';
import { makeQueryKey } from '../../utils/entity-states/query-key';
import { ModuleEntityService } from '../services/module.entity.service';
import { TableLoadingSpinnerComponent } from '../../utils/component/table-loading-spinner/table-loading-spinner';
import { SchoolClassEntity } from '../../core/global-enums/school-class/school-class.entity';
import { SchoolClassEntityService } from '../../core/global-enums/school-class/services/school-class.entity.service';
import {
  MultiComboEntitySearchComponent
} from '../../utils/component/multi-combo-entity-search/multi-combo-entity-search.component';
import { FormDialogModuleComponent } from '../form-dialog-module/form-dialog-module.component';
import {
  ReorderDialogModuleComponent,
  ReorderDialogData,
  ReorderDialogResult
} from '../reorder-dialog-module/reorder-dialog-module.component';
import {
  ConfirmDialogRawComponent,
  SimpleConfirmData
} from '../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import { LocalStorageService } from '../../core/services/local-storage.service';
import {
  GlobalMetaDataEntityService
} from '../../global-metadata/services/global-metadata.entity.service';
import { GlobalMetaDataEntity } from '../../global-metadata/global-metadata.entity';
import { BaseFilterComponent } from '../../utils/component/base-filter-component/base-filter-component';
import { environment } from '../../../environments/environment';
import { HasApprovalState, StateBadgeComponent } from '../../core/components/state-badge/state-badge.component';
import { LiveAnnouncer } from '@angular/cdk/a11y';

export const myCustomTooltipDefaults: MatTooltipDefaultOptions = {
  showDelay: 1000,
  hideDelay: 100,
  touchendHideDelay: 100,
};

@Component({
  selector: 'pixl-main-module-management',
  imports: [
    MatIcon,
    MatSortModule,
    MatTableModule,
    MatButtonModule,
    MatSelectModule,
    MatTooltipModule,
    ReactiveFormsModule,
    FormsModule,
    FreeFilterInputComponent,
    TranslocoModule,
    MatTooltip,
    AsyncPipe,
    MatPaginator,
    TableLoadingSpinnerComponent,
    SlicePipe,
    StateFilterCBComponent,
    MultiComboEntitySearchComponent,
    DatePipe,
    StateBadgeComponent
  ],
  templateUrl: './main-module.component.html',
  styleUrl: './main-module.component.scss',
  providers: [{ provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: myCustomTooltipDefaults }]
})
export class MainModuleComponent extends BaseFilterComponent implements OnInit, AfterViewInit {
  private store = inject(Store);
  private widgetService = inject(WidgetMessageService);
  private dialog = inject(MatDialog);
  private translocoService = inject(TranslocoService);
  private _liveAnnouncer = inject(LiveAnnouncer);

  public moduleService = inject(ModuleEntityService);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private learningUnitService = inject(LearningUnitEntityService);
  private schoolClassService = inject(SchoolClassEntityService);
  private localStorageService = inject(LocalStorageService);
  private globalMetadataService = inject(GlobalMetaDataEntityService);
  private labelLearningUnits: string[] = [];

  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;
  public readonly moduleStateClass = ModuleEntity as unknown as new () => HasApprovalState;
  modules$!: Observable<ModuleEntity[]>;
  deletedIds$ = new BehaviorSubject<string[]>([]);

  learningUnits$!: Observable<LearningUnitEntity[]>;
  consumers$!: Observable<ConsumerEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;
  schoolClasses$!: Observable<SchoolClassEntity[]>;

  public editor$ = this.store.select(AuthState.getEditor);

  selectedLearningUnits: LearningUnitEntity[] = [];
  currentPageLearningUnits = 1;
  pageSizeLearningUnits = 20;
  searchQueryLearningUnits: string | null = null;

  public refresh$ = new BehaviorSubject<void>(undefined);
  public isAbuConsumerSelected = false;
  public cantonOptions: { name: string, label: string }[] = [];

  displayedColumns: string[] = ['actions', 'id', 'preTitle', 'title', 'description', 'consumer', 'subjects', 'schoolClasses', 'learningUnits', 'status', 'created_at'];

  totalItems = 1000;
  private sortString = 'title';

  private destroyRef = inject(DestroyRef);
  private readonly pageChange$ = toObservable(this.pageChange);

  ngOnInit() {
    this.loadRelationData();

    this.editor$.subscribe(editor => {
      if (editor && editor.role === 'zavAdmin') {
        this.displayedColumns.push('content');
      }
    });

    this.modules$ = combineLatest([
      this.pageChange$,
      this.refresh$,
      this.deletedIds$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }, , deletedIds]) => {
        const query: Record<string, string | number> = {
          'include': 'consumer,subjects,learningUnits,schoolClasses',
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
          'sort': this.sortString,
        };

        if (this.freeFilter?.trim()?.length >= this.freeFilterLen) {
          query['filter[idOrTitleLike]'] = this.freeFilter.trim();
        }

        if (this.consumerFilter?.length > 0) {
          query['filter[consumer][id]'] = this.consumerFilter.join(',');
        }

        if (this.subjectFilter?.length > 0) {
          query['filter[subjects][name]'] = this.subjectFilter.join(',');
        }

        if (this.learningUnitFilter?.length > 0) {
          query['filter[learningUnits][id]'] = this.learningUnitFilter.join(',');
        }

        if (this.statusFilter?.length > 0) {
          query['filter[status]'] = this.statusFilter.trim();
        }

        return from(this.moduleService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.moduleService.allByQuery(query))),
          map((modules: ModuleEntity[]) => modules.filter(m => !deletedIds.includes(m.id))),
          map((modules: ModuleEntity[]) => {
            if (!this.selectedCanton) return modules;
            const canton = this.selectedCanton;
            return modules
              .filter(m => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const kantone = (m.consumerData as any)?.kantone;
                return Array.isArray(kantone) && kantone.some((k: { name: string }) => k.name === canton);
              })
              .sort((a, b) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const orderA = (a.order as any)?.[canton] ?? '';
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const orderB = (b.order as any)?.[canton] ?? '';
                return orderA.localeCompare(orderB);
              });
          }),
          tap(() => {
            const state = this.store.selectSnapshot(this.moduleService.stateClass) as EntityStateModel<ModuleEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state?.meta?.[queryKey]?.page?.total ?? 0;
          })
        );
      })
    );
  }

  ngAfterViewInit(): void {
    this.paginator().page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: PageEvent) => {
      this.pageChange.set({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  private loadRelationData(): void {
    const learningUnitQuery = { 'page[size]': this.pageSizeLearningUnits, 'page[number]': this.currentPageLearningUnits, 'sort': 'title' };

    this.learningUnits$ = from(
      this.learningUnitService.ensureLoaded(learningUnitQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.learningUnitService.allByQuery(learningUnitQuery)).pipe(
          map(paginated => {
            return [...this.selectedLearningUnits, ...paginated].filter(
              (lu, index, self) => index === self.findIndex(s => s.id === lu.id)
            );
          })
        )
      )
    );

    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(
      this.consumerService.ensureLoaded(consumerQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.consumerService.allByQuery(consumerQuery))
      )
    );

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(
      this.subjectService.ensureLoaded(subjectQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.subjectService.allByQuery(subjectQuery))
      )
    );

    const schoolClassQuery = { sort: 'grade' };
    this.schoolClasses$ = from(
      this.schoolClassService.ensureLoaded(schoolClassQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.schoolClassService.allByQuery(schoolClassQuery))
      )
    );
  }

  onAdd(): void {
    const dialogRef = this.dialog.open(FormDialogModuleComponent, {
      width: '1200px',
      maxWidth: 'none',
      data: {}
    });

    dialogRef.afterClosed().subscribe(async (result: Partial<ModuleEntity> | undefined) => {
      if (result) {
        const newEntityInstance = Object.assign(new ModuleEntity(), result);
        await this.moduleService.create(newEntityInstance);
        this.store.dispatch(new InvalidateLoadedQueries());
        this.refresh$.next();

        this.showMessage('Modul erfolgreich erstellt.');
      }
    });
  }

  onEdit(module: ModuleEntity): void {
    this.currentSelectedRow.set(module.id);

    const dialogRef = this.dialog.open(FormDialogModuleComponent, {
      width: '1200px',
      maxWidth: 'none',
      data: { ...module }
    });

    dialogRef.afterClosed().subscribe(async (updatedModule: ModuleEntity | undefined) => {
      if (!updatedModule) return;
      await firstValueFrom(this.moduleService.update(updatedModule));
      this.refresh$.next();
      this.showMessage('Modul erfolgreich editiert.');
    });
  }

  async onCopy(module: ModuleEntity) {
    this.currentSelectedRow.set(module.id);
    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: String(module.title),
            headerText: 'Modul kopieren',
            afterNameText: 'wirklich kopieren?'
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    const copyEntityInstance = Object.assign(new ModuleEntity(), module);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (copyEntityInstance as any).consumer = {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...(copyEntityInstance as any).consumer,
      type: 'consumers',
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (copyEntityInstance as any).id = undefined;
    copyEntityInstance.title = `${copyEntityInstance.title} (Kopie)`;
    await this.moduleService.create(copyEntityInstance);
    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.showMessage('Modul erfolgreich dupliziert.');
  }

  onReorder(module: ModuleEntity): void {
    this.currentSelectedRow.set(module.id);

    const consumerId = module.consumer?.[0]?.id ?? null;

    const dialogRef = this.dialog.open<ReorderDialogModuleComponent, ReorderDialogData, ReorderDialogResult>(
      ReorderDialogModuleComponent,
      {
        width: '600px',
        data: { module, consumerId, selectedCanton: this.selectedCanton }
      }
    );

    dialogRef.afterClosed().subscribe(async (result: ReorderDialogResult | undefined) => {
      if (!result) return;

      await firstValueFrom(
        this.moduleService.reorder(module.id, result.afterModuleId, consumerId, this.selectedCanton)
      );

      this.store.dispatch(new InvalidateLoadedQueries());
      this.refresh$.next();

      this.showMessage('Modul erfolgreich verschoben.');
    });
  }

  async onDelete(module: ModuleEntity) {
    if (!module?.id) {
      console.error('ID is undefined!', module);
      return;
    }

    this.currentSelectedRow.set(module.id);

    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: String(module.title),
            headerText: 'Modul löschen',
            afterNameText: 'wirklich löschen?'
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    await firstValueFrom(this.moduleService.delete(module.id));

    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.showMessage('Modul erfolgreich gelöscht.');
  }

  private showMessage(message: string, messageTye: messageTypes = 'mtSuccess'): void {
    this.widgetService.setMessage({
      message,
      messageTye
    });
  }

  public announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    }
    else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
    this.sortData(sortState);
  }

  public sortData(sort: Sort): void {
    this.sortString = sort.direction === 'desc' ? `-${sort.active}` : sort.active;
    this.refresh$.next();
  }

  getSubjectNames(subjects: { id: string }[]): string[] {
    if (!subjects?.length) return [];

    const state = this.store.selectSnapshot(this.subjectService.stateClass);

    return subjects.map(subject => {
      const entity = this.subjectService.stateClass.byId(state)(subject.id);
      return entity?.name || subject.id;
    });
  }

  getSchoolClassNames(schoolClasses: { id: string }[]): string[] {
    if (!schoolClasses?.length) return [];

    const state = this.store.selectSnapshot(this.schoolClassService.stateClass);

    return schoolClasses.map(schoolClass => {
      const entity = this.schoolClassService.stateClass.byId(state)(schoolClass.id);
      return entity?.grade ? `Klasse ${entity.grade}` : schoolClass.id;
    });
  }

  getLearningUnitNames(learningUnits: { id: string }[]): string[] {
    if (!learningUnits?.length) return [];

    const state = this.store.selectSnapshot(this.learningUnitService.stateClass);

    return learningUnits.map(learningUnit => {
      const entity = this.learningUnitService.stateClass.byId(state)(learningUnit.id);
      return entity?.title || learningUnit.id;
    });
  }

  getConsumerName(consumerId: string): string {
    if (!consumerId) return '---';

    const state = this.store.selectSnapshot(this.consumerService.stateClass);
    const consumer = this.consumerService.stateClass.byId(state)(consumerId);

    return consumer?.name || consumerId;
  }

  getKantonLabels(module: ModuleEntity): string[] {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const kantone = (module.consumerData as any)?.kantone;
    if (!Array.isArray(kantone)) return [];
    return kantone.map((k: { name: string, label?: string }) => k.label || k.name);
  }

  getProfessionLabels(module: ModuleEntity): string[] {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const professions = (module.consumerData as any)?.professions_abu_paired;
    if (!Array.isArray(professions)) return [];
    return professions.map((p: { name: string, label?: string }) => p.label || p.name);
  }

  public override setConsumerFilter(selected: string[]): void {
    super.setConsumerFilter(selected);
    this.updateAbuConsumerFlag();
  }

  private updateAbuConsumerFlag(): void {
    const state = this.store.selectSnapshot(this.consumerService.stateClass);
    this.isAbuConsumerSelected = this.consumerFilter.some(id => {
      const consumer = this.consumerService.stateClass.byId(state)(id);
      return consumer?.name === 'ABU';
    });

    if (this.isAbuConsumerSelected) {
      this.loadCantonOptions();
      if (!this.displayedColumns.includes('kantone')) {
        const consumerIndex = this.displayedColumns.indexOf('consumer');
        this.displayedColumns.splice(consumerIndex + 1, 0, 'kantone');
      }
      if (!this.displayedColumns.includes('professions')) {
        const kantoneIndex = this.displayedColumns.indexOf('kantone');
        this.displayedColumns.splice(kantoneIndex + 1, 0, 'professions');
      }
      this.displayedColumns = this.displayedColumns.filter(c => c !== 'subjects');
    }
    else {
      this.cantonOptions = [];
      this.selectedCanton = null;
      this.displayedColumns = this.displayedColumns.filter(c => c !== 'kantone' && c !== 'professions');
      if (!this.displayedColumns.includes('subjects')) {
        const consumerIndex = this.displayedColumns.indexOf('consumer');
        this.displayedColumns.splice(consumerIndex + 1, 0, 'subjects');
      }
    }
  }

  private async loadCantonOptions(): Promise<void> {
    const cantonsQuery = { 'filter[name]': 'kantone' };
    await this.globalMetadataService.ensureLoaded(cantonsQuery);

    const cantonsMeta = this.store.selectSnapshot(
      this.globalMetadataService.allByQuery(cantonsQuery)
    ) as GlobalMetaDataEntity[];

    this.cantonOptions = [];
    if (cantonsMeta.length > 0 && cantonsMeta[0].fieldConfig) {
      for (const field of cantonsMeta[0].fieldConfig) {
        if (field.subFieldConf && field.subFieldConf.length > 0) {
          this.cantonOptions = field.subFieldConf.map(c => ({ name: c.name, label: c.label || c.name }));
        }
      }
    }
  }

  receiveLearningUnitData(selected: LearningUnitEntity[] | null) {
    this.selectedLearningUnits = selected ?? [];
    const selectedUnits = (selected ?? []).map(lu => lu.id);
    this.labelLearningUnits = (selected ?? []).map(lu => lu.title);
    this.setLearningUnitFilter(selectedUnits);
  }

  async receiveLearningUnitQuery(query: string) {
    this.searchQueryLearningUnits = query?.trim() || null;

    if (this.searchQueryLearningUnits && this.searchQueryLearningUnits.length > 3) {
      // search mode
      const searchParams = { 'filter[titleLike]': this.searchQueryLearningUnits };
      await this.learningUnitService.ensureLoaded(searchParams);

      this.learningUnits$ = this.store.select(this.learningUnitService.allByQuery(searchParams)).pipe(
        map(results => results)
      );
    }
    else {
      // default paginated mode
      const learningUnitPageQuery = { 'page[size]': this.pageSizeLearningUnits, 'page[number]': this.currentPageLearningUnits, 'sort': 'title' };
      this.learningUnits$ = this.store.select(this.learningUnitService.allByQuery(learningUnitPageQuery)).pipe(
        map(paginated => {
          return [...this.selectedLearningUnits, ...paginated]
            .filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id));
        })
      );
    }
  }

  async loadMoreLearningUnits(event: { pageNumber: number, pageSize: number }) {
    if (!this.searchQueryLearningUnits) {
      const learningUnitPageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'title',
      };

      await this.learningUnitService.ensureLoaded(learningUnitPageQuery);

      const currentLearningUnits = await firstValueFrom(this.learningUnits$ ?? of([]));

      this.learningUnits$ = this.store.select(this.learningUnitService.allByQuery(learningUnitPageQuery)).pipe(
        map(paginated => {
          return [
            ...this.selectedLearningUnits,
            ...currentLearningUnits,
            ...paginated,
          ].filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id));
        })
      );
    }
  }

  get learningUnitLabels(): string {
    return this.labelLearningUnits?.length ?
      this.labelLearningUnits.join(', ') :
      'Alle Lerneinheiten';
  }

  isModulePreviewActive(module: ModuleEntity): boolean {
    const consumerId = (module.consumer as ConsumerEntity | undefined)?.id;
    if (!consumerId) return false;

    const state = this.store.selectSnapshot(this.consumerService.stateClass);
    const consumer = this.consumerService.stateClass.byId(state)(consumerId) as ConsumerEntity;
    return !!(consumer?.consumerData)?.module_preview_active;
  }

  getModulePreviewLink(module: ModuleEntity): string | null {
    const consumerId = (module.consumer as ConsumerEntity | undefined)?.id;
    if (!consumerId || !module.id) return null;

    const state = this.store.selectSnapshot(this.consumerService.stateClass);
    const consumer = this.consumerService.stateClass.byId(state)(consumerId);
    const consumerUrl = consumer?.url;
    if (!consumerUrl) return null;

    const baseUrl = consumerUrl.replace(/\/+$/, '');
    return (
      baseUrl +
      '/internal/module/' +
      module.id +
      '?token=' +
      environment.learningSectionPreviewToken
    );
  }

  trackById(index: number, module: ModuleEntity): string {
    return module.id;
  }
}
